import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Form } from "@/react/components/shared/uikit/Form";
import {
  DISPLAY_STYLE_DEFAULT_COLORS,
  DISPLAY_STYLE_OPTIONS,
  localeRoot,
} from "../../../constants";
import { FieldContainer } from "../../components/FieldContainer";
import { AvatarImageField } from "../Fields/AvatarImageField";
import { FieldWithCounter } from "../Fields/FieldWithCounter";

const localeFields = "builder.testimonials.fields";
const translationRoot = [localeRoot, localeFields].join(".");
const defaultFieldClassName = "!mt-0 !py-0";

interface TestimonialFormProps {
  disabled: boolean;
  keyFormId: string;
  name: string;
}

const dataTestId = "paywall-customization-testimonial-fields";

const generateNameFields = (name: string) => ({
  quote: [name, "quote"].join("."),
  name: [name, "name"].join("."),
  avatar_image: [name, "avatar_image"].join("."),
  avatar_image_url: [name, "avatar_image_url"].join("."),
  headline: [name, "headline"].join("."),
  display_style_type: [name, "display_style.type"].join("."),
  display_style_color: [name, "display_style.color"].join("."),
});

export const TestimonialForm = ({
  name,
  disabled,
  keyFormId,
}: TestimonialFormProps) => {
  const fields = useMemo(() => generateNameFields(name), [name]);

  const { watch, getValues, setValue } = useFormContext();
  const displayStyleOptionSelected = watch(fields.display_style_type);
  const initialDisplayStyleOption = useRef<string>(
    getValues(fields.display_style_type),
  );

  useEffect(() => {
    if (initialDisplayStyleOption.current !== displayStyleOptionSelected) {
      setValue(
        fields.display_style_color,
        displayStyleOptionSelected === DISPLAY_STYLE_OPTIONS.STARS
          ? DISPLAY_STYLE_DEFAULT_COLORS.STARS
          : DISPLAY_STYLE_DEFAULT_COLORS.QUOTATION_MARK,
      );
    }
    initialDisplayStyleOption.current = displayStyleOptionSelected;
  }, [displayStyleOptionSelected]);

  const testimonialName: string = watch(fields.name) || "";
  const quote: string = watch(fields.quote) || "";
  const headline: string = watch(fields.headline) || "";

  const validation = () => {
    if (testimonialName === "" && quote === "" && headline === "") {
      return t([translationRoot, "errors.fulfill_one_field_at_least"]);
    }

    return true;
  };

  return (
    <FieldContainer dataTestId={dataTestId}>
      <FieldWithCounter
        name={fields.quote}
        label={t([translationRoot, "quote"])}
        maxLength={500}
        errorMessage={t([translationRoot, "errors.quote_max_length"])}
      >
        <Form.Item
          className={defaultFieldClassName}
          name={fields.quote}
          translationRoot={translationRoot}
          hidePlaceholder
          hideBorder
          hideLabel
          fullWidth={false}
          hideDescription
          rules={{
            validate: validation,
            maxLength: {
              value: 500,
              message: t([translationRoot, "errors.quote_max_length"]),
            },
          }}
        >
          <Form.TextArea
            key={keyFormId}
            name={fields.quote}
            data-testid="testimonial-quote-input"
          />
        </Form.Item>
      </FieldWithCounter>
      <Form.Item
        className={defaultFieldClassName}
        name={fields.name}
        labelClassName="!font-medium"
        label={t([translationRoot, "image_and_name"])}
        translationRoot={translationRoot}
        hidePlaceholder
        hideBorder
        fullWidth={false}
        hideDescription
        rules={{ validate: validation }}
      >
        <Form.Input
          key={keyFormId}
          prefix={
            <AvatarImageField
              signedIdField={fields.avatar_image}
              urlField={fields.avatar_image_url}
              testimonialName={testimonialName}
            />
          }
          prefixWrapperClassName="px-3"
          data-testid="testimonial-name-input"
        />
      </Form.Item>
      <Form.Item
        className={defaultFieldClassName}
        name={fields.headline}
        labelClassName="!font-medium"
        label={t([translationRoot, "headline"])}
        translationRoot={translationRoot}
        hidePlaceholder
        hideBorder
        fullWidth={false}
        hideDescription
        rules={{ validate: validation }}
      >
        <Form.Input key={keyFormId} data-testid="testimonial-headline-input" />
      </Form.Item>
      <Form.Item
        className={defaultFieldClassName}
        labelClassName="!font-medium"
        label={t([translationRoot, "display_style"])}
        translationRoot={translationRoot}
        hideBorder
        fullWidth={false}
        hideDescription
        hidePlaceholder
      >
        <div className="flex flex-row items-center gap-x-3">
          <Form.SelectNative
            key={keyFormId}
            name={fields.display_style_type}
            options={[
              {
                label: t([translationRoot, "display_style_options.stars"]),
                value: DISPLAY_STYLE_OPTIONS.STARS,
              },
              {
                label: t([
                  translationRoot,
                  "display_style_options.quotation_mark",
                ]),
                value: DISPLAY_STYLE_OPTIONS.QUOTATION_MARK,
              },
            ]}
            disabled={disabled}
            data-testid="testimonial-display-style-select"
          />
          <div>
            <Form.ColorPickerV2
              key={keyFormId}
              name={fields.display_style_color}
            />
          </div>
        </div>
      </Form.Item>
    </FieldContainer>
  );
};
